import { Button } from "react-bootstrap"
import Icon from "../common/components/Icon"
import { Link, matchPath, Outlet, useLocation } from "react-router-dom"
import { Menu, MenuItem, Sidebar, SubMenu } from "react-pro-sidebar"
import { Children, createContext, useContext, useMemo, useState } from "react";
import { useApp } from "../common/components/AppProvider";
import useNetwork from "../common/hooks/networkHook";
import { useEffect } from "react";

const NavBarContext = createContext({ collapsed: false, toggled: false });
const useNavBar = () => useContext(NavBarContext);

const NavBarProvider = ({ children }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [toggled, setToggled] = useState(false);

    const value = useMemo(() => ({ collapsed, toggled, setCollapsed, setToggled }), [collapsed, toggled]);

    return <NavBarContext.Provider value={value}>
        {children}
    </NavBarContext.Provider>
}

export const NavSidebar = ({ children, ...props }) => {
    const { toggled, collapsed, setToggled } = useNavBar();

    return <Sidebar toggled={toggled} collapsed={collapsed} onBackdropClick={() => setToggled(false)} breakPoint="992px" style={{ border: 'none' }} {...props}>{children}</Sidebar>
}

export const NavMenu = ({ children, ...props }) => {
    const { collapsed } = useNavBar();
    return <Menu renderExpandIcon={({ open }) => {
        let intent = '';
        if (collapsed) {
            intent = document.dir === 'rtl' ? 'left' : 'right'
        } else {
            intent = open ? 'up' : 'down'
        }
        return <Icon intent={intent} style={{ fontSize: 'small' }}></Icon>
    }} {...props}>
        {children}
    </Menu>
}

export const NavSubMenu = ({ children, label, ...props }) => {
    if (!label || Children.count(children) === 0) {
        return children;
    }
    return <SubMenu label={label} {...props}>{children}</SubMenu>
}

function useExactMatchRoute(pattern) {
  const location = useLocation();
  if (pattern === '') {
    return false;
  }
  if (pattern.endsWith('?')) {
    return matchPath({ path: pattern.slice(0, pattern.lastIndexOf('/')), end: true }, location.pathname)
            || matchPath({ path: pattern.slice(0, -1), end: true }, location.pathname);
  }
  return matchPath({ path: pattern, end: true }, location.pathname);
}

export const NavMenuItem = ({ path, altpath, ...props }) => {
    const match = useExactMatchRoute(path || '');
    const altmatch = useExactMatchRoute(altpath || '');
    return <MenuItem active={match || altmatch} component={<Link to={path}></Link>}{...props}></MenuItem>
}

const NavBarImpl = ({ sidebar, image, style, bodyStyle }) => {
    const { collapsed, setToggled, setCollapsed } = useNavBar();

    return  <div className="w-100 d-flex flex-column flex-lg-row" style={{ height: "calc(100vh)" }}>
        <div className="h-100 d-none d-lg-flex flex-md-column" style={style}>
            <div className="d-flex flex-row align-items-center">
                <div className="text-center p-2" style={{ flex: 1 }}>
                    <div style={{ width: collapsed ? '32px' : undefined, overflow: "hidden" }}>
                        {image}
                    </div>
                </div>
                <div>
                    <Button onClick={() => setCollapsed(collapsed => !collapsed)} variant="outline-light" style={{ border: 'none' }}><Icon intent={collapsed ? "right" : "left"}></Icon></Button>
                </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
                {sidebar}
            </div>
        </div>
        <div className="d-flex flex-row p-1 d-lg-none" style={{ ...style, justifyContent: 'space-between', alignItems: 'center' }}>
            {sidebar}
            <div>
                {image}
            </div>
            <Button onClick={() => setToggled(toggled => !toggled)} variant="outline-light" style={{ border: 'none' }}><Icon variant="solid" kind="bars"></Icon></Button>
        </div>
        <div className="px-2 d-flex flex-column" style={{ ...bodyStyle, flex: 1, overflowY: 'auto' }}>
            <Outlet></Outlet>
        </div>
    </div>
}

export const NavBarWrapper = ({ ...props }) => {
    const { on } = useApp();
    const network = useNetwork();

    useEffect(() => {
        const cleanup = [
            on('addFCMToken', data => {
                network.post('/api/v1/addFCMToken', { username: data.username, token: data.token });
            }),
            on('removeFCMToken', data => {
                fetch('/api/v1/removeFCMToken', {
                    method: 'POST',
                    headers: {
                        Authorization: data.jwt,
                        Accept: 'application/json',
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ username: data.username, token: data.token })
                })
            })
        ];
        return () => cleanup.forEach(c => c());
    }, [network, on])

    return <NavBarProvider>
       <NavBarImpl {...props}></NavBarImpl>
    </NavBarProvider>
}